import * as R from "ramda";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindInstance } from "redux-mvc";
import Select from "react-select";

import { noop } from "utils/General";

import { actions } from "ui-kit/Form/model";
import { getValue, getDropdownOptions } from "ui-kit/Form/selectors";
import { FIELD_TYPES } from "ui-kit/Form/constants";

import { WithFormInstanceConsumer } from "ui-kit/Form/View/Context";

import { Div, DownFilledIcon } from "components/Base";
import { fixFontColor } from "../../FieldTypes/utils";

const ArrowRenderer = ({ onMouseDown }) => (
  <Div display="row.center.center" onMouseDown={onMouseDown}>
    <DownFilledIcon sizeWFS={5} />
  </Div>
);

const Option = props => {
  return (
    <div
      {...props.innerProps}
      style={{
        padding: "5px 0px 5px 0px",
        backgroundColor: props.isFocused ? "#FAFAFA" : "#FFFFFF",
        cursor: props.isDisabled ? "default" : "pointer"
      }}
    >
      <div style={props.getStyles("option", props)}>{props.children}</div>
    </div>
  );
};

const decorate = R.compose(
  WithFormInstanceConsumer({ fieldType: FIELD_TYPES.DROPDOWN }),
  connect(
    (state, props) => ({
      value: getValue(state, props),
      options: getDropdownOptions(state, props)
    }),
    bindInstance({
      onChange: actions.setFieldValue,
      setIniValue: actions.setIniValue,
      onBlur: actions.blurField
    })
  )
);

export const generateDropdownStyles = ({
  customStyles = {},
  usePortal,
  useBadgeStyle,
  width,
  bg,
  menuHeight,
  usePortalHeaderStyle
}) => {
  const styles = {
    ...customStyles,
    control: provided => ({
      ...provided,
      backgroundColor: bg
    }),
    multiValueLabel: provided => ({
      ...provided,
      whiteSpace: "inherit",
      wordBreak: "break-all"
    })
  };

  if (menuHeight) {
    styles.menuList = provided => ({
      ...provided,
      maxHeight: menuHeight
    });
  }

  styles.multiValue = (styles, { data }) => {
    const bg =
      data.backgroundColor || data.color || styles.backgroundColor || "#EBEBEB";
    const fontColor = data.textColor || styles.color || "#000000";
    const fixedFontColor = fixFontColor(bg, fontColor);

    return {
      ...styles,
      backgroundColor: bg,
      color: fixedFontColor
    };
  };

  styles.multiValueLabel = (styles, { data }) => {
    const bg =
      data.backgroundColor || data.color || styles.backgroundColor || "#EBEBEB";
    const fontColor = data.textColor || styles.color || "#000000";
    const fixedFontColor = fixFontColor(bg, fontColor);

    return {
      ...styles,
      color: fixedFontColor
    };
  };

  if (usePortal) {
    styles.menu = provided => ({ ...provided, zIndex: 9999 });

    styles.menuPortal = styles => ({
      ...styles,
      zIndex: 9999
    });
  }

  if (width) {
    styles.container = provided => ({
      ...provided,
      width
    });
  }

  styles.option = (styles, { data }) => {
    const bg = data.backgroundColor || data.color || styles.backgroundColor;
    const fontColor = data.textColor || styles.color;
    const fixedFontColor = fixFontColor(bg, fontColor);
    return {
      ...styles,
      backgroundColor: bg,
      color: fixedFontColor
    };
  };

  styles.singleValue = (styles, { data }) => {
    const bg = data.backgroundColor || data.color || styles.backgroundColor;
    const fontColor = data.textColor || styles.color;
    const fixedFontColor = fixFontColor(bg, fontColor);
    return {
      ...styles,
      backgroundColor: bg,
      color: fixedFontColor
    };
  };

  // handle: override styles if using badge style
  if (useBadgeStyle) {
    styles.option = (styles, { data }) => {
      const bg = data.backgroundColor || data.color || "#EBEBEB";
      const fontColor = data.textColor || "#000000";
      const fixedFontColor = fixFontColor(bg, fontColor);

      const updatedStyles = {
        ...styles,
        backgroundColor: bg,
        color: fixedFontColor,
        width: "auto",
        display: "inline-block",
        borderRadius: "4px",
        fontSize: "12px",
        marginLeft: "12px",
        height: "100%",
        padding: "7px"
      };

      return {
        ...updatedStyles,
        ...(customStyles && customStyles.option
          ? customStyles.option(updatedStyles)
          : {})
      };
    };

    styles.singleValue = (styles, { data }) => {
      const bg =
        data.backgroundColor ||
        data.color ||
        styles.backgroundColor ||
        "#EBEBEB";
      const fontColor = data.textColor || styles.color || "#000000";
      const fixedFontColor = fixFontColor(bg, fontColor);

      const updatedStyles = {
        borderRadius: "4px",
        fontSize: "12px",
        padding: "7px",
        ...styles,
        backgroundColor: bg,
        color: fixedFontColor
      };

      return {
        ...updatedStyles,
        ...(customStyles && customStyles.singleValue
          ? customStyles.singleValue(updatedStyles)
          : {})
      };
    };
  }

  if (usePortalHeaderStyle) {
    styles.control = styles => ({
      ...styles,
      backgroundColor: "transparent",
      border: "unset",
      color: "#343434",
      fontSize: "16px",
      fontWeight: 600,
      wordWrap: "break-word",
      paddingLeft: "8px",
      cursor: "pointer"
    });
    styles.option = styles => ({
      ...styles,
      cursor: "pointer"
    });
  }

  return styles;
};

export const generateComponents = ({
  useBadgeStyle,
  usePortalHeaderStyle,
  compToadd
}) => {
  let components = {};
  if (useBadgeStyle) {
    components.Option = Option;
  }
  if (usePortalHeaderStyle) {
    if (!R.isEmpty(compToadd)) {
      components.IndicatorSeparator = compToadd.IndicatorSeparator;
      components.Control = compToadd.Control;
      return components;
    }
  }
  return components;
};

export const UCDropdown = ({
  value = "",
  iniValue,
  fieldId,
  fieldType,
  multipleSelect = false,
  options = [],
  usePortal,
  width,
  onChange = noop,
  setIniValue = noop,
  onBlur = noop,
  onFocus = noop,
  instanceId, // eslint-disable-line no-unused-vars
  bg = "white",
  placeholder = "Select...",
  disabled = false,
  cellDropdownAuto = false,
  customStyles = {},
  useBadgeStyle = false,
  usePortalHeaderStyle = false,
  components: compToadd = null,
  isSearchable = true,
  hideSelectedOptions = false,
  isClearable = true
}) => {
  useEffect(() => {
    setIniValue(iniValue, {
      meta: { fieldType, fieldId, multipleSelect }
    });
  }, []);

  const styles = generateDropdownStyles({
    customStyles,
    usePortal,
    useBadgeStyle,
    width,
    bg,
    usePortalHeaderStyle
  });

  const components = generateComponents({
    useBadgeStyle,
    usePortalHeaderStyle,
    compToadd
  });

  return (
    <Select
      placeholder={placeholder || "Select..."}
      menuPortalTarget={usePortal ? document.body : undefined}
      arrowRenderer={ArrowRenderer}
      isMulti={multipleSelect}
      onChange={val =>
        onChange(val, {
          meta: { fieldType, fieldId, multipleSelect }
        })
      }
      onBlur={() => onBlur(null, { meta: { fieldId, fieldType } })}
      value={value}
      options={options}
      styles={styles}
      isDisabled={disabled}
      menuPlacement={cellDropdownAuto ? "auto" : undefined}
      onFocus={() => onFocus(null, { meta: { fieldId, fieldType } })}
      components={components}
      isSearchable={isSearchable}
      hideSelectedOptions={hideSelectedOptions}
      isClearable={isClearable}
    />
  );
};

export const Dropdown = decorate(UCDropdown);
